import { supabase } from '@/integrations/supabase/client';
import { buildHrZones, type HrZone, type HrZoneId } from '@/lib/heartRateZones';
import { structureRunGuideFromInput, parseRunPlan } from '@/lib/runPlanParser';
import { normalizeCoachPlanText } from '@/lib/runningCoach';

const ZONE_PACE_HINTS: Record<HrZoneId, string> = {
  z1: 'very easy recovery jog',
  z2: 'easy conversational pace',
  z3: 'steady tempo, short phrases only',
  z4: 'threshold effort, comfortably hard',
  z5: 'hard interval / VO2 pace',
};

export type StructureRunGuideOptions = {
  ageYears?: number | null;
  maxHr?: number | null;
};

/** Client-side structure; returns null if input could not be improved or recognized. */
export function buildStructuredRunGuideLocally(
  raw: string,
  opts?: StructureRunGuideOptions,
): string | null {
  const trimmedIn = normalizeCoachPlanText(raw).trim();
  if (!trimmedIn) return null;

  try {
    const structured = structureRunGuideFromInput(trimmedIn, opts);
    const trimmedOut = structured.trim();
    if (trimmedOut && trimmedOut !== trimmedIn) return trimmedOut;
    if (parseRunPlan(trimmedIn).hasStructure) return trimmedIn;
  } catch (err) {
    console.warn('[buildStructuredRunGuideLocally] failed:', err instanceof Error ? err.message : String(err));
  }
  return null;
}

export async function requestStructuredRunGuideFromAi(
  raw: string,
  opts?: StructureRunGuideOptions,
): Promise<string | null> {
  try {
    const zones = buildHrZones({ ageYears: opts?.ageYears, maxHr: opts?.maxHr }).zones;
    const zoneTable = zones
      .map((z) => `${z.id.toUpperCase()}: ${z.lowerBpm}-${z.upperBpm} bpm (${z.description})`)
      .join('\n');

    const { data, error } = await supabase.functions.invoke('structure-run-guide', {
      body: {
        input: normalizeCoachPlanText(raw).slice(0, 800),
        zoneTable,
        maxHr: buildHrZones({ ageYears: opts?.ageYears, maxHr: opts?.maxHr }).maxHr,
      },
    });
    if (error) {
      console.warn('[structure-run-guide]', error.message ?? String(error));
      return null;
    }
    const plan = typeof data?.plan === 'string' ? data.plan.trim() : '';
    return plan.length > 8 ? plan : null;
  } catch (err) {
    console.warn('[structure-run-guide] invoke failed:', err instanceof Error ? err.message : String(err));
    return null;
  }
}

export async function structureRunGuideForCoach(
  raw: string,
  opts?: StructureRunGuideOptions,
): Promise<{ plan: string; source: 'local' | 'ai' } | null> {
  const trimmed = normalizeCoachPlanText(raw).trim();
  if (!trimmed) return null;

  try {
    const local = buildStructuredRunGuideLocally(trimmed, opts);
    if (local) return { plan: local, source: 'local' };

    const ai = await Promise.race([
      requestStructuredRunGuideFromAi(trimmed, opts),
      new Promise<null>((r) => setTimeout(() => r(null), 8000)),
    ]);
    if (ai?.trim()) {
      const aiPlan = ai.trim();
      if (parseRunPlan(aiPlan).hasStructure || aiPlan.length > trimmed.length) {
        return { plan: aiPlan, source: 'ai' };
      }
    }
  } catch (err) {
    console.warn('[structureRunGuideForCoach] failed:', err instanceof Error ? err.message : String(err));
  }

  return null;
}

export function zoneLineSuffix(zone: HrZone, paceHint?: string): string {
  const hint = paceHint || ZONE_PACE_HINTS[zone.id];
  return `${zone.label}, ${zone.lowerBpm}–${zone.upperBpm} bpm — ${hint}`;
}
